import { slugify } from 'core/utils/strings';
import { getCerealsGrowthStageTranslation } from 'core/utils/translations/growth-stage';
import type { RegionalOverviewDiseaseRiskTechnician } from 'querys/regional-overview/regional-overview.query.models';
import { RegionalOverviewFilterType } from '@features/regional-overview/regional-overview.models';

export interface FilterLabelContext {
  technicians?: RegionalOverviewDiseaseRiskTechnician[];
}

export const FILTER_SECTION_TITLE_KEY: Partial<Record<RegionalOverviewFilterType, string>> = {
  [RegionalOverviewFilterType.RISK_LEVEL]: 'pages.regional_overview.filters.risk_level.title',
  [RegionalOverviewFilterType.CROPS]: 'pages.regional_overview.filters.crop.title',
  [RegionalOverviewFilterType.TECHNICIAN]: 'pages.regional_overview.filters.technician.title',
  [RegionalOverviewFilterType.GROWTH_STAGES]: 'pages.regional_overview.filters.growth_stage.title',
  [RegionalOverviewFilterType.DISEASE]: 'pages.regional_overview.filters.disease.title',
  [RegionalOverviewFilterType.VARIETY]: 'pages.regional_overview.filters.variety.title',
  [RegionalOverviewFilterType.SOWING_DATE]: 'pages.regional_overview.filters.sowing_date.title',
  [RegionalOverviewFilterType.GROUPS]: 'pages.regional_overview.filters.network.title',
  [RegionalOverviewFilterType.ZONES]: 'pages.regional_overview.filters.zone.title',
  [RegionalOverviewFilterType.PREVIOUS_CROP]: 'pages.regional_overview.filters.previous_crop.title',
  [RegionalOverviewFilterType.TILLAGE]: 'pages.regional_overview.filters.tillage.title',
  [RegionalOverviewFilterType.CLASS]: 'pages.regional_overview.filters.class.title'
};

export const FILTER_ALL_OPTION_KEY: Partial<Record<RegionalOverviewFilterType, string>> = {
  [RegionalOverviewFilterType.RISK_LEVEL]: 'pages.disease_risk.all_risks',
  [RegionalOverviewFilterType.CROPS]: 'pages.regional_overview.regional_risk.all_crops',
  [RegionalOverviewFilterType.TECHNICIAN]: 'pages.regional_overview.regional_risk.all_technicians',
  [RegionalOverviewFilterType.GROWTH_STAGES]: 'pages.regional_overview.regional_risk.all_growth_stages',
  [RegionalOverviewFilterType.DISEASE]: 'pages.regional_overview.regional_risk.all_diseases',
  [RegionalOverviewFilterType.VARIETY]: 'pages.regional_overview.regional_risk.all_varieties',
  [RegionalOverviewFilterType.GROUPS]: 'pages.regional_overview.digital_traps.all_networks',
  [RegionalOverviewFilterType.ZONES]: 'pages.regional_overview.regional_mycotoxin.all_zones',
  [RegionalOverviewFilterType.PREVIOUS_CROP]: 'pages.regional_overview.regional_mycotoxin.previous_crop',
  [RegionalOverviewFilterType.TILLAGE]: 'pages.regional_overview.regional_mycotoxin.tillage',
  [RegionalOverviewFilterType.CLASS]: 'pages.regional_overview.regional_mycotoxin.class'
};

const RISK_LEVEL_LABEL_KEY: Record<string, string> = {
  '0': 'pages.disease_risk.low',
  '1': 'pages.disease_risk.medium',
  '2': 'pages.disease_risk.high',
  '3': 'pages.disease_risk.no_risk_legend'
};

export const buildFilterOptionLabelKey = (filterType: RegionalOverviewFilterType, value: string, context?: FilterLabelContext): string => {
  switch (filterType) {
    case RegionalOverviewFilterType.CROPS:
      return `general.crops.${value}`;
    case RegionalOverviewFilterType.DISEASE:
      return `general.diseases.${slugify(value)}.full`;
    case RegionalOverviewFilterType.GROWTH_STAGES:
      return getCerealsGrowthStageTranslation(value);
    case RegionalOverviewFilterType.RISK_LEVEL:
      return RISK_LEVEL_LABEL_KEY[value] ?? value;
    case RegionalOverviewFilterType.TECHNICIAN:
      return context?.technicians?.find(technician => technician.id === value)?.name ?? value;
    case RegionalOverviewFilterType.ZONES:
      return value.split('_')[1] ?? value;
    case RegionalOverviewFilterType.TILLAGE:
      return `pages.regional_overview.regional_mycotoxin.filters.tillage.${slugify(value)}`;
    case RegionalOverviewFilterType.VARIETY:
    case RegionalOverviewFilterType.SOWING_DATE:
    default:
      return value;
  }
};
